import { Draft, produce } from 'immer';
import { calcInferenceSetDerivatives } from './calcInferenceSetDerivatives';
import { calcCarePhaseCostDerivatives } from './calcCarePhaseCostDerivatives';
import { calcSupportProviderDerivatives } from './calcSupportProviderDerivatives';
import { calcSurveyDerivatives } from './calcSurveyDerivatives';
import { fullName } from './fullName';
import { calcCareEnvironmentsDerivatives } from './calcCareEnvironmentsDerivatives';
import { calcFundingSourceDerivatives } from './calcFundingSourceDerivatives';
import { applyDiscountsToCareEnvironmentCosts } from './applyDiscountsToCareEnvironmentCosts';
import { cloneDeep } from 'lodash';

export function calcClientDerivatives(
  mutableClient: Draft<Client>,
  clientPartner: Client | null,
): Draft<Client> {
  return produce(mutableClient, draftClient => {
    if (clientPartner) {
      addMutableClientPartner(draftClient, clientPartner);
    }
    runAllClientDerivativeCalculations(draftClient);
  });
}

function runAllClientDerivativeCalculations(mutableClient: Draft<Client>): Draft<Client> {
  calcClientDerivativesImpl(mutableClient);
  return mutableClient;
}

export function calcClientDerivativesImpl(mutableClient: Draft<Client>): Draft<Client> {
  mutableClient.clientFullName = fullName(mutableClient);
  mutableClient.cachedTimestamp = new Date();

  calcSurveyDerivatives(mutableClient); // must be first
  calcCareEnvironmentsDerivatives(mutableClient);
  calcSupportProviderDerivatives(mutableClient);
  calcInferenceSetDerivatives(mutableClient);
  applyDiscountsToClient(mutableClient);
  calcCarePhaseCostDerivatives(mutableClient);
  calcFundingSourceDerivatives(mutableClient);

  return mutableClient;
}

/**
 * Re-applies discounts based on current funding sources.
 * This is needed for modeling the impact of potential funding sources on care environment costs.
 * Note: This is already performed on the server side in fetchClientLocalCareEnvironmentCosts.
 */
function applyDiscountsToClient(mutableClient: Draft<Client>): void {
  if (!mutableClient.clientLocalCareEnvironmentCosts) {
    return;
  }

  mutableClient.clientLocalCareEnvironmentCosts = applyDiscountsToCareEnvironmentCosts(
    mutableClient.clientLocalCareEnvironmentCosts as LocalCareEnvironmentCosts,
    mutableClient.multipleFundingSources,
  ) as Draft<LocalCareEnvironmentCosts>;
}

function addMutableClientPartner(mutableClient: Draft<Client>, clientPartner: Client): void {
  const clientPartnerCopy = cloneDeep(clientPartner);
  mutableClient.mutableClientPartner = clientPartnerCopy;
}
